import React, { useCallback, useEffect, useState } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  XCircleIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'

const TABS = [
  { id: 'integrations', label: 'Venue integrations' },
  { id: 'functions', label: 'Functions' },
  { id: 'interface', label: 'Interface data' },
  { id: 'devices', label: 'Gateway devices' },
]

const emptyIntegrationForm = () => ({
  membership_system: '',
  gaming_system: '',
  visitor_management_system: '',
  pig_azure_relay_uri: '',
  pig_azure_relay_entity: '',
  pig_azure_relay_auth_enabled: '1',
  pig_azure_relay_issuer_name: '',
  pig_azure_relay_issuer_secret: '',
  pig_command_api_path: 'PerformPiGCommand',
  pig_encryption_enabled: '0',
  azure_service_bus_uri: '',
  azure_service_bus_issuer_name: '',
  azure_service_bus_issuer_secret: '',
  sid_storage_conn_string: '',
})

export default function VenuePsgGatewayModal({ venue, onClose }) {
  const venueId = venue?.venue_id
  const [tab, setTab] = useState('integrations')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [integrationForm, setIntegrationForm] = useState(emptyIntegrationForm)
  const [saving, setSaving] = useState(false)
  const [fnForm, setFnForm] = useState(null)
  const [ifaceForm, setIfaceForm] = useState(null)

  const load = useCallback(async () => {
    if (!venueId) return
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/venues/${encodeURIComponent(venueId)}/psg-gateway`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Load failed')
      setData(json.data)
      const v = json.data?.venue || {}
      setIntegrationForm({
        membership_system: v.membership_system ?? '',
        gaming_system: v.gaming_system ?? '',
        visitor_management_system: v.visitor_management_system ?? '',
        pig_azure_relay_uri: v.pig_azure_relay_uri ?? v.azure_service_bus_uri ?? '',
        pig_azure_relay_entity: v.pig_azure_relay_entity ?? '',
        pig_azure_relay_auth_enabled: v.pig_azure_relay_auth_enabled ?? '1',
        pig_azure_relay_issuer_name: v.pig_azure_relay_issuer_name ?? v.azure_service_bus_issuer_name ?? '',
        pig_azure_relay_issuer_secret:
          v.pig_azure_relay_issuer_secret_set || v.azure_service_bus_issuer_secret_set ? '********' : '',
        pig_command_api_path: v.pig_command_api_path ?? 'PerformPiGCommand',
        pig_encryption_enabled: v.pig_encryption_enabled ?? '0',
        azure_service_bus_uri: v.azure_service_bus_uri ?? '',
        azure_service_bus_issuer_name: v.azure_service_bus_issuer_name ?? '',
        azure_service_bus_issuer_secret: v.azure_service_bus_issuer_secret_set ? '********' : '',
        sid_storage_conn_string: v.sid_storage_conn_string_set ? '********' : '',
      })
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [venueId])

  useEffect(() => {
    load()
  }, [load])

  const saveIntegrations = async () => {
    setSaving(true)
    setError(null)
    try {
      const body = { ...integrationForm }
      if (body.azure_service_bus_issuer_secret === '********') delete body.azure_service_bus_issuer_secret
      if (body.pig_azure_relay_issuer_secret === '********') delete body.pig_azure_relay_issuer_secret
      if (body.sid_storage_conn_string === '********') delete body.sid_storage_conn_string
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/venues/${encodeURIComponent(venueId)}/psg-gateway/integrations`),
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveFunction = async () => {
    if (!fnForm) return
    setSaving(true)
    try {
      const isEdit = !!fnForm.id
      const url = isEdit
        ? getLocalApiUrl(`/api/admin/psg-gateway/functions/${fnForm.id}`)
        : getLocalApiUrl('/api/admin/psg-gateway/functions')
      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fnForm),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      setFnForm(null)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveInterface = async () => {
    if (!ifaceForm) return
    setSaving(true)
    try {
      const isEdit = !!ifaceForm.id
      const url = isEdit
        ? getLocalApiUrl(`/api/admin/psg-gateway/interface-data/${ifaceForm.id}`)
        : getLocalApiUrl('/api/admin/psg-gateway/interface-data')
      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ifaceForm),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      setIfaceForm(null)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const deleteFunction = async (id) => {
    if (!confirm('Delete this function row?')) return
    await authFetch(getLocalApiUrl(`/api/admin/psg-gateway/functions/${id}`), { method: 'DELETE' })
    await load()
  }

  const deleteInterface = async (id) => {
    if (!confirm('Delete this interface row?')) return
    await authFetch(getLocalApiUrl(`/api/admin/psg-gateway/interface-data/${id}`), { method: 'DELETE' })
    await load()
  }

  const schema = data?.schema
  const devices = data?.devices || []
  const functions = data?.functions || []
  const interfaceData = data?.interface_data || []
  const defaultDeviceId = devices[0]?.device_id || ''

  if (!venue) return null

  return (
    <div className="fixed inset-0 bg-gray-600/50 z-50 overflow-y-auto">
      <div className="relative top-8 mx-auto p-0 border w-11/12 max-w-5xl shadow-xl rounded-lg bg-white mb-8">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50 rounded-t-lg">
          <div className="flex items-center gap-2">
            <ServerStackIcon className="h-6 w-6 text-teal-700" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">PSG Gateway — {venue.name}</h3>
              <p className="text-xs text-gray-500 font-mono">{venueId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={load} className="p-2 rounded hover:bg-gray-200" title="Refresh">
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button type="button" onClick={onClose} className="p-2 rounded hover:bg-gray-200">
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex border-b px-2 gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id ? 'border-teal-600 text-teal-800' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 min-h-[320px]">
          {error && (
            <div className="mb-4 p-3 rounded bg-red-50 text-red-800 text-sm">{error}</div>
          )}
          {loading && !data ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : (
            <>
              {tab === 'integrations' && (
                <div className="space-y-4">
                  {!schema?.venue_psg_columns?.length ? (
                    <p className="text-sm text-amber-800 bg-amber-50 p-3 rounded">
                      PSG venue columns not provisioned on this database. Run tenant migration{' '}
                      <code className="text-xs">088_psg_gateway_pg.sql</code>.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        Values returned to the gateway as <strong>system_config</strong> and{' '}
                        <strong>azure_config</strong> for this venue (via device{' '}
                        <code className="text-xs">venue_id_at_register</code>).
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          ['membership_system', 'Membership system'],
                          ['gaming_system', 'Gaming system'],
                          ['visitor_management_system', 'Visitor management system'],
                          ['pig_azure_relay_uri', 'PiG relay URI (HTTPS)'],
                          ['pig_azure_relay_entity', 'PiG relay entity (WCF)'],
                          ['pig_azure_relay_auth_enabled', 'PiG relay auth enabled (0/1)'],
                          ['pig_azure_relay_issuer_name', 'PiG relay issuer name'],
                          ['pig_azure_relay_issuer_secret', 'PiG relay issuer secret'],
                          ['pig_command_api_path', 'PiG command API path'],
                          ['pig_encryption_enabled', 'PiG encryption enabled (0/1)'],
                          ['azure_service_bus_uri', 'Legacy Azure bus URI (dual-write)'],
                          ['azure_service_bus_issuer_name', 'Legacy Azure issuer name'],
                          ['azure_service_bus_issuer_secret', 'Legacy Azure issuer secret'],
                          ['sid_storage_conn_string', 'SID storage connection string'],
                        ].map(([key, label]) => (
                          <div key={key}>
                            <label className="block text-xs text-gray-600 mb-1">{label}</label>
                            <input
                              className="input-field w-full font-mono text-sm"
                              type={key.includes('secret') || key.includes('conn_string') ? 'password' : 'text'}
                              value={integrationForm[key] ?? ''}
                              onChange={(e) =>
                                setIntegrationForm((prev) => ({ ...prev, [key]: e.target.value }))
                              }
                              placeholder={key.includes('secret') ? 'Leave blank to keep; enter new to change' : ''}
                            />
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={saveIntegrations}
                        className="px-4 py-2 rounded-md bg-teal-700 text-white text-sm hover:bg-teal-800 disabled:opacity-50"
                      >
                        Save venue integrations
                      </button>
                    </>
                  )}
                </div>
              )}

              {tab === 'functions' && (
                <div className="space-y-4">
                  {!schema?.psg_functions ? (
                    <p className="text-sm text-amber-800">Table <code>psg_functions</code> not found (migration 088).</p>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600">
                          Scheduled extracts per gateway device (DimPatron, sessions, meter, …).
                        </p>
                        <button
                          type="button"
                          className="text-sm px-3 py-1 rounded bg-blue-100 text-blue-800"
                          onClick={() =>
                            setFnForm({
                              device_id: defaultDeviceId,
                              function_code: '',
                              interface_type: 'IGT',
                              function_flag: '1',
                              mode_interval: '0',
                              mode_timer: '1',
                              schedule_time: '06:00:00',
                              folder_path: '',
                              file_prefix: '',
                              field_delimiter: 'PIPE',
                              delete_source_file: '0',
                              is_file_with_header: '0',
                            })
                          }
                        >
                          <PlusIcon className="h-4 w-4 inline" /> Add function
                        </button>
                      </div>
                      {fnForm && (
                        <div className="border rounded p-3 bg-gray-50 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          {[
                            'device_id',
                            'function_code',
                            'interface_type',
                            'schedule_time',
                            'file_prefix',
                            'field_delimiter',
                            'function_flag',
                            'mode_interval',
                            'mode_timer',
                          ].map((k) => (
                            <div key={k}>
                              <label className="text-xs text-gray-500">{k}</label>
                              <input
                                className="input-field w-full font-mono text-xs"
                                value={fnForm[k] ?? ''}
                                onChange={(e) => setFnForm((p) => ({ ...p, [k]: e.target.value }))}
                              />
                            </div>
                          ))}
                          <div className="col-span-full flex gap-2">
                            <button type="button" onClick={saveFunction} className="px-3 py-1 bg-teal-700 text-white rounded text-sm">
                              {fnForm.id ? 'Update' : 'Create'}
                            </button>
                            <button type="button" onClick={() => setFnForm(null)} className="px-3 py-1 bg-gray-200 rounded text-sm">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="overflow-x-auto border rounded">
                        <table className="min-w-full text-xs divide-y">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-2 text-left">device_id</th>
                              <th className="px-2 py-2 text-left">function_code</th>
                              <th className="px-2 py-2 text-left">schedule</th>
                              <th className="px-2 py-2 text-left">file_prefix</th>
                              <th className="px-2 py-2">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {functions.map((row) => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-2 py-1 font-mono truncate max-w-[120px]" title={row.device_id}>
                                  {row.device_id?.slice(0, 8)}…
                                </td>
                                <td className="px-2 py-1">{row.function_code}</td>
                                <td className="px-2 py-1">{row.schedule_time || '—'}</td>
                                <td className="px-2 py-1">{row.file_prefix || '—'}</td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  <button type="button" onClick={() => setFnForm({ ...row })} className="text-blue-600 mr-2">
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button type="button" onClick={() => deleteFunction(row.id)} className="text-red-600">
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {functions.length === 0 && (
                              <tr>
                                <td colSpan={5} className="px-2 py-4 text-gray-500 text-center">
                                  No functions for devices linked to this venue
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {tab === 'interface' && (
                <div className="space-y-4">
                  {!schema?.third_party_interface_data ? (
                    <p className="text-sm text-amber-800">Table <code>third_party_interface_data</code> not found.</p>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="text-sm px-3 py-1 rounded bg-blue-100 text-blue-800"
                        onClick={() =>
                          setIfaceForm({
                            interface_type: 'MEMBERSHIP',
                            interface_name: 'IGT',
                            name: 'ExtractFilePath',
                            value: '',
                            device_id: null,
                          })
                        }
                      >
                        <PlusIcon className="h-4 w-4 inline" /> Add row
                      </button>
                      {ifaceForm && (
                        <div className="border rounded p-3 bg-gray-50 grid grid-cols-2 gap-2">
                          {['interface_type', 'interface_name', 'name', 'value', 'device_id'].map((k) => (
                            <div key={k} className={k === 'value' ? 'col-span-2' : ''}>
                              <label className="text-xs text-gray-500">{k}</label>
                              <input
                                className="input-field w-full text-sm"
                                value={ifaceForm[k] ?? ''}
                                placeholder={k === 'device_id' ? 'empty = tenant-wide' : ''}
                                onChange={(e) =>
                                  setIfaceForm((p) => ({
                                    ...p,
                                    [k]: e.target.value === '' && k === 'device_id' ? null : e.target.value,
                                  }))
                                }
                              />
                            </div>
                          ))}
                          <div className="col-span-2 flex gap-2">
                            <button type="button" onClick={saveInterface} className="px-3 py-1 bg-teal-700 text-white rounded text-sm">
                              {ifaceForm.id ? 'Update' : 'Create'}
                            </button>
                            <button type="button" onClick={() => setIfaceForm(null)} className="px-3 py-1 bg-gray-200 rounded text-sm">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="overflow-x-auto border rounded">
                        <table className="min-w-full text-xs divide-y">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-2 text-left">type</th>
                              <th className="px-2 py-2 text-left">name</th>
                              <th className="px-2 py-2 text-left">value</th>
                              <th className="px-2 py-2 text-left">device</th>
                              <th className="px-2 py-2">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {interfaceData.map((row) => (
                              <tr key={row.id}>
                                <td className="px-2 py-1">{row.interface_type}</td>
                                <td className="px-2 py-1">{row.name}</td>
                                <td className="px-2 py-1 max-w-xs truncate" title={row.value}>
                                  {row.value || '—'}
                                </td>
                                <td className="px-2 py-1 font-mono text-[10px]">
                                  {row.device_id ? `${String(row.device_id).slice(0, 8)}…` : 'all'}
                                </td>
                                <td className="px-2 py-1">
                                  <button type="button" onClick={() => setIfaceForm({ ...row })} className="text-blue-600 mr-1">
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button type="button" onClick={() => deleteInterface(row.id)} className="text-red-600">
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {tab === 'devices' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    PSG Gateway PCs registered for this tenant with{' '}
                    <code className="text-xs">venue_id_at_register</code> = this venue (or unset). Approve
                    devices and encryption keys in the <strong>platform admin</strong> console.
                  </p>
                  <div className="overflow-x-auto border rounded">
                    <table className="min-w-full text-sm divide-y">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Device</th>
                          <th className="px-3 py-2 text-left">Terminal</th>
                          <th className="px-3 py-2 text-left">Venue at register</th>
                          <th className="px-3 py-2 text-left">Approval</th>
                          <th className="px-3 py-2 text-left">DDR trigger</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((d) => (
                          <tr key={d.device_id}>
                            <td className="px-3 py-2 font-mono text-xs">{d.device_id}</td>
                            <td className="px-3 py-2">{d.terminal_name || d.registration_username || '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs">{d.venue_id_at_register || '—'}</td>
                            <td className="px-3 py-2">{d.approval_status || '—'}</td>
                            <td className="px-3 py-2">{d.ddr_trigger_name || '—'}</td>
                          </tr>
                        ))}
                        {devices.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                              No gateway devices linked to this venue
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
